/**
 * Export survey analysis to markdown
 */

import { QuestionAnalysis, SurveyAnalysisResult } from './surveyApi';

function progressBar(percentage: number, width: number = 10): string {
  const filled = Math.round((percentage / 100) * width);
  const empty = width - filled;
  return '█'.repeat(filled) + '░'.repeat(empty);
}

function getDistributionLabel(item: any): string {
  return String(item.choice ?? item.rating ?? item.level ?? item.score ?? item.value ?? '');
}

function exportQuestionToMarkdown(qa: QuestionAnalysis): string {
  const lines: string[] = [];

  // Question header
  lines.push(`### ${qa.question_text}`);
  lines.push(`*${qa.total_responses} responses*`);
  lines.push('');

  // TL;DR
  if (qa.tldr) {
    lines.push(`**TL;DR:** ${qa.tldr}`);
    lines.push('');
  }

  // Strategic Insight
  if (qa.synthesis) {
    lines.push(`**Strategic Insight:** ${qa.synthesis}`);
    lines.push('');
  }

  // Counter Arguments
  if (qa.counter_arguments && qa.counter_arguments.length > 0) {
    lines.push(`**Worth Noting:**`);
    for (const arg of qa.counter_arguments) {
      lines.push(`- ${arg}`);
    }
    lines.push('');
  }

  // Distribution
  if (qa.distribution && qa.distribution.length > 0) {
    lines.push('**Response Distribution:**');
    lines.push('');
    if (qa.type === 'percentage_allocation') {
      // Percentage allocation: table per option with tiers
      for (const item of qa.distribution) {
        const choice = (item as any).choice || '';
        lines.push(`**${choice}**`);
        lines.push('');
        lines.push('| Tier | Bar | Count | % |');
        lines.push('|------|-----|------:|----:|');
        const tiers = [
          { label: 'Active (>0%)', data: (item as any).active },
          { label: 'Primary (≥50%)', data: (item as any).primary },
          { label: 'Loyalist (≥75%)', data: (item as any).loyalist },
        ];
        for (const tier of tiers) {
          if (tier.data) {
            lines.push(`| ${tier.label} | ${progressBar(tier.data.percentage)} | ${tier.data.count} | ${tier.data.percentage.toFixed(1)}% |`);
          }
        }
        lines.push('');
      }
    } else {
      // Standard distribution as table
      const filtered = qa.distribution.filter((item: any) => item.count > 0).slice(0, 10);
      lines.push('| Option | Bar | Count | % |');
      lines.push('|--------|-----|------:|----:|');
      for (const item of filtered) {
        const label = getDistributionLabel(item);
        const pct = (item as any).percentage ?? 0;
        const count = (item as any).count ?? 0;
        lines.push(`| ${label} | ${progressBar(pct)} | ${count} | ${pct.toFixed(1)}% |`);
      }
      lines.push('');
    }
  }

  // Yes/No
  if (qa.yes_count !== undefined && qa.no_count !== undefined) {
    lines.push('**Response Distribution:**');
    lines.push('');
    lines.push('| Answer | Bar | Count | % |');
    lines.push('|--------|-----|------:|----:|');
    lines.push(`| Yes | ${progressBar(qa.yes_percentage ?? 0)} | ${qa.yes_count} | ${(qa.yes_percentage ?? 0).toFixed(1)}% |`);
    lines.push(`| No | ${progressBar(qa.no_percentage ?? 0)} | ${qa.no_count} | ${(qa.no_percentage ?? 0).toFixed(1)}% |`);
    lines.push('');
  }

  // Rankings
  if (qa.rankings && qa.rankings.length > 0) {
    lines.push('**Rankings:**');
    lines.push('');
    const hasAvg = qa.rankings.some((i: any) => i.average_rank !== undefined);
    const hasNet = qa.rankings.some((i: any) => i.net_score !== undefined);
    let header = '| Rank | Item |';
    let divider = '|-----:|------|';
    if (hasAvg) { header += ' Avg Rank |'; divider += '---------:|'; }
    if (hasNet) { header += ' Net Score |'; divider += '----------:|'; }
    lines.push(header);
    lines.push(divider);
    for (const [idx, item] of qa.rankings.slice(0, 10).entries()) {
      const name = (item as any).item || (item as any).option || '';
      let row = `| ${idx + 1} | ${name} |`;
      if (hasAvg) row += ` ${(item as any).average_rank ?? '-'} |`;
      if (hasNet) { const ns = (item as any).net_score; row += ` ${ns !== undefined ? (ns > 0 ? '+' : '') + ns : '-'} |`; }
      lines.push(row);
    }
    lines.push('');
  }

  // Matrix Items
  if (qa.items && qa.items.length > 0) {
    lines.push('**Item Ratings:**');
    lines.push('');
    lines.push('| Item | Bar | Rating |');
    lines.push('|------|-----|-------:|');
    const maxScale = Math.max(...qa.items.map((i: any) => i.average), 7);
    for (const item of qa.items.slice(0, 10)) {
      const pct = ((item as any).average / maxScale) * 100;
      lines.push(`| ${(item as any).item} | ${progressBar(pct)} | ${(item as any).average?.toFixed(1)} |`);
    }
    lines.push('');
  }

  // Conjoint Preferences
  if (qa.preferences && qa.preferences.length > 0) {
    lines.push('**Preferences:**');
    lines.push('');
    lines.push('| Choice | Bar | Count | % |');
    lines.push('|--------|-----|------:|----:|');
    const filtered = qa.preferences.filter((item: any) => item.count > 0).slice(0, 10);
    for (const item of filtered) {
      lines.push(`| ${(item as any).choice} | ${progressBar((item as any).percentage)} | ${(item as any).count} | ${(item as any).percentage?.toFixed(1)}% |`);
    }
    lines.push('');
  }

  // Image Hotspot Products
  if (qa.products && qa.products.length > 0) {
    lines.push('**Product Clicks:**');
    lines.push('');
    lines.push('| Rank | Product | First Picks | Total Clicks |');
    lines.push('|-----:|---------|------------:|-------------:|');
    for (const [idx, item] of qa.products.slice(0, 10).entries()) {
      lines.push(`| ${idx + 1} | ${(item as any).product} | ${(item as any).first_choice_count} | ${(item as any).total_clicks} |`);
    }
    lines.push('');
  }

  lines.push('---');
  lines.push('');

  return lines.join('\n');
}

export function exportAnalysisToMarkdown(analysis: SurveyAnalysisResult, surveyName?: string): string {
  const lines: string[] = [];

  // Title
  lines.push(`# AI Analysis${surveyName ? `: ${surveyName}` : ''}`);
  lines.push(`*${analysis.responses_analyzed} responses analyzed*`);
  lines.push('');

  // Analysis Goal
  if (analysis.analysis_goal) {
    lines.push(`**Analysis Goal:** ${analysis.analysis_goal}`);
    lines.push('');
  }

  // Overall Synthesis
  if (analysis.survey_synthesis?.markdown) {
    lines.push('## Overall Synthesis');
    lines.push('');
    lines.push(analysis.survey_synthesis.markdown);
    lines.push('');
  }

  // Question-by-Question
  if (analysis.question_analyses && Object.keys(analysis.question_analyses).length > 0) {
    lines.push('## Question-by-Question Analysis');
    lines.push('');
    for (const qa of Object.values(analysis.question_analyses)) {
      lines.push(exportQuestionToMarkdown(qa));
    }
  }

  return lines.join('\n');
}

export function downloadAnalysisAsMarkdown(analysis: SurveyAnalysisResult, surveyName?: string): void {
  const markdown = exportAnalysisToMarkdown(analysis, surveyName);
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(surveyName || 'analysis').replace(/[^a-zA-Z0-9]/g, '_')}_analysis.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}